import { useState } from 'react';
import { Button, Select, SelectItem, Text, Title } from '@tremor/react';
import Modal from '../../components/Modal';
import type { User, WoStatusLetter, ScheduleResult } from '../../api/types';
import type { AgendaCard } from './Agenda';
import { setWoSchedule } from '../../api/service';
import { ptimeToHHMM, hhmmToPtime, HALF_HOUR_SLOTS } from '../../lib/scheduleTime';
import { t } from '../../lib/i18n';

const STATUSES: WoStatusLetter[] = ['A', 'S', 'W', 'H', 'C'];

/**
 * Client write-gate mirror of the server's triple gate: writes must be enabled
 * AND the user must hold service:schedule (owner/manager by role default, or an
 * explicit override). /api/me only emits override perms, so owner/manager are
 * recognized by role — same idiom as Discrepancias' canHeal.
 */
export function canSchedule(user: User): boolean {
  return !!user.features?.writes_enabled &&
    (user.role === 'owner' || user.role === 'manager' || (user.permissions ?? []).includes('service:schedule'));
}

/** ss-status has no line-level 'bko'; the header uses A/W/S/C/H only. */
function statusLetterOf(k: AgendaCard['status']): WoStatusLetter {
  return (k === 'bko' ? 'W' : k) as WoStatusLetter;
}

/**
 * ScheduleModal — the gated reschedule form. Edits a WO's appointment
 * (date / time / status / header tech) and issues one setWoSchedule call.
 * When writes are off it renders read-only (no Save). A multi-field form, so it
 * uses the base Modal, not ConfirmDialog.
 */
export default function ScheduleModal({
  user, card, techs, onSaved, onClose, onView360,
}: {
  user: User;
  card: AgendaCard;
  techs: { code: string; name: string }[];
  onSaved: (r: NonNullable<ScheduleResult['data']>) => void;
  onClose: () => void;
  onView360: (sid: number, tnum: number) => void;
}) {
  const writable = canSchedule(user);
  const c = t.service.cockpit.agenda.edit;
  const [date, setDate] = useState(card.promise_date || '');
  const [time, setTime] = useState(ptimeToHHMM(card.promise_time)); // '' = sin hora
  const [status, setStatus] = useState<WoStatusLetter>(statusLetterOf(card.status));
  const [tech, setTech] = useState(card.techCode);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = () => {
    setSaving(true);
    setErr(null);
    setWoSchedule({ sid: card.sid, tnum: card.tnum, status, pdate: date, ptime: hhmmToPtime(time), tech })
      .then((r) => {
        if (r.ok && r.data) onSaved(r.data);
        else setErr('write failed');
      })
      .catch((e: Error) => setErr(e.message ?? String(e)))
      .finally(() => setSaving(false));
  };

  return (
    <Modal onClose={onClose} ariaLabel={`${c.title} #${card.tnum}`} size="md">
      <Title className="text-base mb-3">{c.title} #{card.tnum}</Title>
      <div className="space-y-3">
        <label className="block text-sm">{c.date}
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} disabled={!writable}
            className="mt-1 w-full border border-tremor-border rounded px-2 py-1 bg-tremor-background text-tremor-content-strong" />
        </label>
        <label className="block text-sm">{c.time}
          <input type="time" step={1800} list="agenda-slots" value={time} onChange={(e) => setTime(e.target.value)} disabled={!writable}
            aria-label={c.time}
            className="mt-1 w-full border border-tremor-border rounded px-2 py-1 bg-tremor-background text-tremor-content-strong" />
          <datalist id="agenda-slots">{HALF_HOUR_SLOTS.map((s) => <option key={s} value={s} />)}</datalist>
        </label>
        <div className="text-sm">{c.status}
          <Select value={status} onValueChange={(v) => setStatus(v as WoStatusLetter)} disabled={!writable} enableClear={false} className="mt-1">
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{t.service.cockpit.status[s]}</SelectItem>)}
          </Select>
        </div>
        <div className="text-sm">{c.tech}
          <Select value={tech} onValueChange={setTech} disabled={!writable} enableClear={false} className="mt-1">
            <SelectItem value="">{c.none}</SelectItem>
            {techs.map((tt) => <SelectItem key={tt.code} value={tt.code}>{tt.name}</SelectItem>)}
          </Select>
        </div>
        {err && <Text className="text-red-500">{err}</Text>}
        {!writable && <Text className="text-tremor-content text-xs">{c.readonly}</Text>}
      </div>
      <div className="flex justify-between mt-4">
        <Button variant="light" onClick={() => onView360(card.sid, card.tnum)}>{c.view360}</Button>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose}>{c.cancel}</Button>
          {writable && <Button onClick={save} loading={saving}>{c.save}</Button>}
        </div>
      </div>
    </Modal>
  );
}
